import React from 'react';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { useDarkMode } from '../../context/DarkModeContext';
import { InternshipPhase } from './InternshipDashboardV2';
import { 
  IoRocketOutline, 
  IoSchoolOutline, 
  IoTrendingUpOutline, 
  IoTrophyOutline, 
  IoAirplaneOutline
} from 'react-icons/io5';
import { motion } from 'framer-motion';
import { useMissionControlActions, actionToContext } from '../../hooks/useMissionControlActions';
import { usePhaseGuidanceFromContext } from '../../context/PhaseGuidanceContext';
import { createLogger } from '../../utils/logger';

const logger = createLogger({ module: 'MissionControl' });

interface MissionControlProps {
  currentPhase: InternshipPhase;
  focus: CareerFocusWithMetrics;
  onQuickAdd?: (context?: {
    title?: string;
    placeholder?: string;
    defaultImpact?: number;
    defaultEngagement?: number;
  }) => void;
}

// Phase metadata that doesn't change per industry
const phaseMetadata: Record<InternshipPhase, {
  name: string;
  icon: React.ReactNode;
  description: string;
}> = {
  onboarding: {
    name: 'Onboarding',
    icon: <IoRocketOutline className="w-5 h-5" />,
    description: 'Focus on understanding and relationship building'
  },
  foundation: {
    name: 'Foundation',
    icon: <IoSchoolOutline className="w-5 h-5" />,
    description: 'Early contributions and learning the ropes'
  },
  growth: {
    name: 'Growth',
    icon: <IoTrendingUpOutline className="w-5 h-5" />,
    description: 'Increasing responsibility and skill development'
  },
  impact: {
    name: 'Impact',
    icon: <IoTrophyOutline className="w-5 h-5" />,
    description: 'Major contributions and leadership opportunities'
  },
  transition: {
    name: 'Transition',
    icon: <IoAirplaneOutline className="w-5 h-5" />,
    description: 'Handoffs and planning for the future'
  }
};

// Get the right phase emoji icon - used for badge display
const getPhaseEmoji = (phase: InternshipPhase): string => {
  const emojiMap: Record<InternshipPhase, string> = {
    onboarding: '🚀',
    foundation: '🏗️',
    growth: '📈',
    impact: '🏆',
    transition: '✈️'
  };
  return emojiMap[phase] || '📋';
};


const MissionControl: React.FC<MissionControlProps> = ({ currentPhase, focus, onQuickAdd }) => {
  const { darkMode } = useDarkMode();
  const industryType = focus.industry_type || 'generic';
  
  // Fetch industry-specific actions from database
  const { actions, loading } = useMissionControlActions(industryType, currentPhase);
  
  // Fetch phase guidance including description from context
  const { guidance } = usePhaseGuidanceFromContext(currentPhase);
  
  logger.debug('MissionControl rendering', { 
    currentPhase, 
    industryType, 
    actionsCount: actions.length,
    loading,
    hasGuidance: !!guidance
  });

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const actionBg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const actionHoverBg = darkMode ? 'hover:border-blue-500' : 'hover:border-blue-400';

  // Phase badge colors
  const phaseBadgeStyle = darkMode 
    ? 'bg-indigo-900 text-indigo-200' 
    : 'bg-indigo-100 text-indigo-800';

  const phaseInfo = phaseMetadata[currentPhase];

  // Show loading state
  if (loading) {
    return (
      <div className={`${bgColor} rounded-xl border ${borderColor} p-6 mb-6`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-gray-300 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={`mission-control ${bgColor} rounded-xl border ${borderColor} p-6 mb-6`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-start justify-between mb-5">
        {/* Phase Indicator */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium ${phaseBadgeStyle}`}>
              <span className="text-base">{getPhaseEmoji(currentPhase)}</span>
              {phaseInfo.name} Phase
            </span>
            <span className={`text-sm ${mutedTextColor}`}>
              {guidance?.description || phaseMetadata[currentPhase].description}
            </span>
          </div>
        </div>

        {/* Quick Add Button */}
        <button
          onClick={() => onQuickAdd?.()}
          className="quick-add-btn px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
        >
          <span>+</span>
          <span>Add Evidence</span>
        </button>
      </div>

      {/* Priority Actions */}
      <div>
        <h3 className={`text-sm font-medium ${mutedTextColor} mb-3`}>This Week's Priorities</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {actions.length > 0 ? (
            actions.map((action) => (
              <motion.button
                key={action.id}
                onClick={() => {
                  const context = actionToContext(action);
                  onQuickAdd?.(context);
                }}
                className={`p-4 rounded-lg border ${borderColor} ${actionBg} text-left transition-all ${actionHoverBg} hover:shadow-sm`}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="mb-2 text-xl">{action.icon_emoji || '📋'}</div>
                <h4 className={`text-sm font-medium ${textColor} mb-1`}>
                  {action.title}
                </h4>
                <p className={`text-xs ${mutedTextColor}`}>
                  {action.description}
                </p>
              </motion.button>
            ))
          ) : (
            // Fallback if no actions are found
            <div className={`col-span-3 text-center py-8 ${mutedTextColor}`}>
              <p>No specific priorities available for this phase.</p>
              <p className="text-sm mt-2">Click "Add Evidence" to document your work!</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MissionControl;